import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { Download, Print } from '@unified-checkout/design-system/icons/index';

import type { ITagProps } from 'libs/design-system/src/types/tag/tag';
import { sharePaymentHandle } from '@app/dashboard/services/inner_layout/payme';
import { PAYME_EVENTS } from '@app/dashboard/constants/tracking_events/payme';
import { useQRCode } from '@app/dashboard/hooks/useQRCode';
import { useUserInfoStore } from '@app/dashboard/store/userInfo';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { notify } from '@app/dashboard/utils/notifier';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import ShareModal from '@app/dashboard/components/shared/modals/ShareModal';

import styles from './payme-share-modal.module.scss';
export interface PayMeShareModalProps {
  open: boolean;
  onClose: () => void;
  paymeLink: string;
  handleName: string;
  branch?: string;
  logoUrl?: string | null;
}

export const PayMeShareModal = ({
  open,
  onClose,
  paymeLink,
  handleName,
  branch,
  logoUrl,
}: PayMeShareModalProps) => {
  const [restTags, setRestTags] = useState(0);

  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const {
    userInfo: { companyName },
  } = useUserInfoStore();
  const modalBodyRef = useRef<HTMLDivElement | null>(null);
  const {
    printModal,
    downloadModalAsPNG,
    isLoading: isQRCodeLoading,
  } = useQRCode(modalBodyRef);

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const { mutateAsync: mutateSharePaymentLink, isPending: isPendingShare } =
    useMutation({
      mutationFn: ({ contacts }: { contacts: string[] }) =>
        sharePaymentHandle({
          share_with: contacts,
          handle_name: handleName,
          ...(branch && { branch }),
        }),
      onSuccess: () => {
        notify.success(t('innerLayout.paymePage.share.sharedSuccess'));
        setRestTags((prev) => prev + 1);
      },
      onError: (
        axiosErr: AxiosError<{ message?: string; detail?: string }>
      ) => {
        let message = t('errors.somethingWentWrong');

        if (axiosErr.response?.data?.message) {
          message = axiosErr.response.data.message;
        } else if (axiosErr.message) {
          message = axiosErr.message;
        } else if (axiosErr.response?.data?.detail) {
          message = axiosErr.response.data.detail;
        }

        amplitude.track(PAYME_EVENTS.PAYME_SHARING_COMPLETION_FAILED, {
          error_description: message,
        });
        notify.error(message);
      },
    });

  const handleShare = async (contacts: ITagProps[]) => {
    try {
      await mutateSharePaymentLink({
        contacts: contacts.map((contact) => contact.text),
      });

      const hasEmail = contacts.some((contact) => contact.text.includes('@'));
      const hasPhone = contacts.some((contact) => !contact.text.includes('@'));

      const methods: string[] = [];
      if (hasEmail) methods.push('email');
      if (hasPhone) methods.push('phone_number');
      const sharingMethod = methods.join(',');

      amplitude.track(PAYME_EVENTS.PAYME_SHARING_COMPLETION_SUCCEEDED, {
        sharing_method: sharingMethod,
      });
    } catch (error: any) {}
  };

  const handleDefaultSocialBtnEvents = (method: string) => {
    const methodName = method === 'qr-code' ? 'qr' : method;
    amplitude.track(PAYME_EVENTS.PAYME_SHARING_COMPLETION_SUCCEEDED, {
      sharing_method: methodName,
    });
  };

  return (
    <div className={styles['payme-share-modal']}>
      

      <ShareModal
        modalProps={{
          isModalOpen: open,
          isOverlayAboveModal: true,
          hasCloseIcon: true,
          handleCloseModal: onClose,
          title: branch
            ? t('innerLayout.paymePage.share.branchTitle', {
                branchName: branch,
              })
            : t('innerLayout.paymePage.share.title'),
          subtitle: t('innerLayout.paymePage.share.subtitle'),
          closeOnClickOutside: true,
        }}
        shareInputProps={{
          link: paymeLink,
          label: t(
            'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.footer.shareModal.actions.shareLink'
          ),
          linkLength: isMobile ? 30 : 60,
        }}
        inputTagsProps={{
          tagsShareBtn: {
            onClickHandler: (tags: ITagProps[]) => handleShare(tags),
            loading: isPendingShare,
            disabled: isPendingShare,
          },
          resetTagsOnShare: restTags,
        }}
        socialBtns={{
          'qr-code': {
            label: t(
              `innerLayout.paymePage.share.${
                isMobile ? 'qrCode' : 'scanPrintQR'
              }`
            ),
          },
        }}
        QRCodeProps={{
          cardProps: {
            cardTitle: companyName,
            cardLogo: logoUrl || '',
            cardSubTitle: branch
              ? `${t('innerLayout.paymePage.share.branchLabel')}: ${branch}`
              : '',
            showPaymobTheme: true,
          },
          modalBodyRef: modalBodyRef,
          modalProps: {
            closeOnClickOutside: true,
            isModalOpen: true,
            handleCloseModal: () => {},
            footerBtn: [
              {
                text: t('innerLayout.paymePage.share.print'),
                variant: 'contained',
                color: 'primary',
                size: 'md',
                leadingIcon: Print,
                onClickHandler: printModal,
              },
              {
                text: t('innerLayout.paymePage.share.download'),
                variant: 'outlined',
                color: 'secondary',
                size: 'md',
                leadingIcon: Download,
                onClickHandler: downloadModalAsPNG,
                loading: isQRCodeLoading,
                disabled: isQRCodeLoading,
              },
            ],
          },
        }}
        handleDefaultSocialBtnEvents={handleDefaultSocialBtnEvents}
      />
    </div>
  );
};
